/**
 * 40. Delivery Notes — list; pick/pack/ship; backorder handling; returns.
 *
 * Arabic and English (C7): every word from the catalogs; statuses agree with
 * the delivery note.
 */
import {
  Card,
  PageHeader,
  DataTable,
  Skeleton,
  StatusBadge,
} from "@/components/ui";
import { PendingButton } from "@/components/PendingAction";
import { useApiList } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { listTitle } from "@/lib/i18n/format";
import { formatDate, formatQuantity } from "@/lib/money";

interface DeliveryNoteRow {
  id: string;
  deliveryNumber: string;
  deliveryDate: string;
  salesOrderNumber: string;
  customerName: string;
  status:
    | "Draft"
    | "Picking"
    | "Packed"
    | "Shipped"
    | "Delivered"
    | "Partial"
    | "Returned";
  lines: number;
}

export function DeliveryNotesPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useApiList<DeliveryNoteRow>(
    ["sales", "delivery-notes"],
    "/sales/delivery-notes",
  );

  return (
    <div>
      <PageHeader
        title={t("sales.deliveryNotes.title")}
        subtitle={t("sales.deliveryNotes.subtitle")}
        actions={
          <PendingButton
            variant="primary"
            reason="deliveryFromOrder"
            slice="ED-W2a"
          >
            {t("sales.deliveryNotes.new")}
          </PendingButton>
        }
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={listTitle(t("sales.deliveryNotes.list"), data.total)}>
          <DataTable<DeliveryNoteRow>
            rows={data.items}
            columns={[
              {
                key: "deliveryNumber",
                label: t("fields.number"),
                render: (r) => (
                  <span className="atlas-mono">{r.deliveryNumber}</span>
                ),
              },
              {
                key: "deliveryDate",
                label: t("fields.date"),
                render: (r) => formatDate(r.deliveryDate),
              },
              {
                key: "salesOrderNumber",
                label: t("sales.deliveryNotes.order"),
                render: (r) => (
                  <span className="atlas-mono">{r.salesOrderNumber}</span>
                ),
              },
              { key: "customerName", label: t("fields.customer") },
              {
                key: "lines",
                label: t("sales.deliveryNotes.lines"),
                numeric: true,
                render: (r) => formatQuantity(r.lines, 0),
              },
              {
                key: "status",
                label: t("fields.status"),
                render: (r) => (
                  <StatusBadge status={r.status} type="DeliveryNote" />
                ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
